import React, { useState, useEffect } from 'react';
import Card from '../../Components/Ui/Card';
import Table from '../../Components/Ui/Table';
import { Search, Calendar, Filter, X, RefreshCw } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useGetSellerPriceLogsQuery } from '../../app/services/priceLogApi';

const PriceLogSeller = () => {
  const { user } = useSelector((state) => state.userInfo);
  const sellerId = user?.id || user?._id;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const {
    data: logsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetSellerPriceLogsQuery(
    {
      sellerId,
      page,
      limit,
      search: debouncedSearch,
      startDate,
      endDate,
      excludePriceType: 'customMargin', // Exclude customMargin price logs for seller
    },
    { skip: !sellerId },
  );

  const logs = logsData?.data || [];
  const pagination = logsData?.pagination || {};

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const formatPrice = (price) => `₹${Number(price || 0).toFixed(2)}`;

  const columns = [
    {
      header: 'Product',
      accessor: (log) => (
        <div>
          <p className="font-medium">{log.productName || 'Unknown Product'}</p>
          <p className="text-xs text-gray-500">{log.priceType || 'Price Update'}</p>
        </div>
      ),
    },
    { header: 'Old Price', accessor: (log) => formatPrice(log.oldPrice) },
    {
      header: 'New Price',
      accessor: (log) => (
        <span className={log.newPrice > log.oldPrice ? 'text-red-600' : 'text-green-600'}>
          {formatPrice(log.newPrice)}
        </span>
      ),
    },
    {
      header: 'Change',
      accessor: (log) => (
        <span className={log.changePercentage >= 0 ? 'text-red-600' : 'text-green-600'}>
          {log.changePercentage >= 0 ? '+' : ''}
          {log.changePercentage?.toFixed(2)}%
        </span>
      ),
    },
    { header: 'Changed By', accessor: (log) => log.changedBy?.name || 'System' },
    { header: 'Date', accessor: (log) => formatDate(log.createdAt) },
  ];

  return (
    <Card title="Price Change History">
      <div className="space-y-4">
        {/* Search & Filter Toggle */}
        <div className="flex flex-col sm:flex-row gap-3 justify-between">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by product"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-secondary hover:bg-gray-50 focus:outline-none focus:ring-2 border-color w-full"
            />
            {searchQuery && (
              <X
                className="absolute right-3 top-2.5 h-4 w-4 text-gray-500 cursor-pointer hover:text-secondary"
                onClick={() => setSearchQuery('')}
              />
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => refetch()}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm flex items-center hover:bg-gray-100"
            >
              <RefreshCw size={18} className={`mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm flex items-center hover:bg-gray-100"
            >
              <Filter size={18} className="mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Date Filter */}
        {isFilterOpen && (
          <div className="bg-gray-50 p-4 rounded-md border border-gray-200 animate-fade-in">
            <div className="text-sm font-medium mb-3">Date Range</div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Calendar size={18} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-md pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="relative flex-1">
                <Calendar size={18} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-md pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            {(startDate || endDate) && (
              <button
                className="mt-3 text-xs text-primary hover:underline"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setPage(1);
                }}
              >
                Clear Dates
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <Table
          columns={columns}
          data={logs}
          isLoading={isLoading}
          emptyMessage="No price changes found"
          keyExtractor={(row) => row._id}
        />

        {/* Pagination */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            Showing {logs.length} of {pagination.totalItems || 0} entries
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm font-medium">
              Page {page} of {pagination.totalPages || 1}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
              disabled={page === (pagination.totalPages || 1) || isFetching}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PriceLogSeller;
